import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDemo, DEMO_STEPS, DEMO_FINAL_PATH } from './DemoContext';

/**
 * DemoOverlay renders:
 * - A thin progress bar at the top of the page
 * - A "Demo Mode" badge
 * - Back / Next navigation buttons at the bottom
 *
 * Only visible when demo mode is active (user came from /how-it-works).
 */
export default function DemoOverlay() {
  const { isDemoActive, exitDemo, currentStepForPath, isFinalStep } = useDemo();
  const location = useLocation();
  const navigate = useNavigate();

  if (!isDemoActive) return null;

  const currentStep = currentStepForPath(location.pathname);
  const isOnFinal = isFinalStep(location.pathname);

  // Not on a demo step page — still show the badge but not the nav
  const showNav = currentStep !== null || isOnFinal;
  const stepIndex = currentStep ? currentStep.step - 1 : (isOnFinal ? DEMO_STEPS.length : -1);
  const totalSteps = DEMO_STEPS.length;
  const progressPct = showNav ? Math.round(((stepIndex + 1) / (totalSteps + 1)) * 100) : 0;

  // Previous step
  const prevStep = stepIndex > 0 ? DEMO_STEPS[stepIndex - 1] : null;
  const prevPath = isOnFinal ? DEMO_STEPS[DEMO_STEPS.length - 1].path : prevStep?.path;

  return (
    <>
      {/* ── Progress bar (top) ── */}
      {showNav && (
        <div style={s.progressBar}>
          <div style={s.progressTrack}>
            <div style={{ ...s.progressFill, width: `${progressPct}%` }} />
          </div>
          <div style={s.progressLabel}>
            {isOnFinal
              ? 'Final Step — Audit & IP Accountability'
              : `Step ${currentStep!.step} of ${totalSteps}: ${currentStep!.title}`}
          </div>
        </div>
      )}

      {/* ── Demo Mode badge (always visible in demo) ── */}
      <div style={s.badge} onClick={exitDemo} title="Click to exit demo mode">
        <span style={s.badgeDot} />
        Demo Mode
        <span style={s.badgeX}>{'\u2715'}</span>
      </div>

      {/* ── Navigation buttons (bottom) ── */}
      {showNav && (
        <div style={s.navBar}>
          {/* Back button */}
          {prevPath ? (
            <button style={s.backBtn} onClick={() => navigate(prevPath)}>
              {'\u2190'} Back
            </button>
          ) : (
            <button style={s.backBtn} onClick={() => navigate('/how-it-works')}>
              {'\u2190'} How It Works
            </button>
          )}

          {/* Step dots */}
          <div style={s.dots}>
            {DEMO_STEPS.map((ds) => (
              <div
                key={ds.step}
                style={{
                  ...s.dot,
                  backgroundColor: ds.step - 1 === stepIndex ? '#4299e1' : ds.step - 1 < stepIndex ? '#48bb78' : '#4a5568',
                }}
                title={`Step ${ds.step}: ${ds.title}`}
                onClick={() => navigate(ds.path)}
              />
            ))}
            <div
              style={{
                ...s.dot,
                backgroundColor: isOnFinal ? '#4299e1' : '#4a5568',
              }}
              title="Final: IP Accountability"
              onClick={() => navigate(DEMO_FINAL_PATH)}
            />
          </div>

          {/* Next / Final buttons */}
          {isOnFinal ? (
            <div style={s.finalBtns}>
              <button
                style={s.nextBtn}
                onClick={() => navigate('/sae')}
              >
                View Full Audit Trail {'\u2192'}
              </button>
              <button
                style={{ ...s.backBtn, marginLeft: 8 }}
                onClick={() => { exitDemo(); navigate('/how-it-works'); }}
              >
                Restart Demo
              </button>
            </div>
          ) : currentStep ? (
            <button style={s.nextBtn} onClick={() => navigate(currentStep.nextPath)}>
              {currentStep.nextLabel} {'\u2192'}
            </button>
          ) : null}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  /* Progress bar */
  progressBar: {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backgroundColor: '#0d1117',
    borderBottom: '1px solid #1a365d',
    padding: '6px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  progressTrack: {
    flex: 1,
    height: 4,
    backgroundColor: '#2d3748',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#4299e1',
    borderRadius: 2,
    transition: 'width 0.4s ease',
  },
  progressLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#a0aec0',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  /* Badge */
  badge: {
    position: 'fixed',
    top: 12,
    right: 16,
    zIndex: 1001,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px',
    borderRadius: 16,
    backgroundColor: '#1a365d',
    border: '1px solid #4299e1',
    fontSize: 11,
    fontWeight: 700,
    color: '#63b3ed',
    cursor: 'pointer',
    userSelect: 'none',
  },
  badgeDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: '#48bb78',
    animation: 'none', // pulsing would need keyframes — keep simple
  },
  badgeX: {
    fontSize: 10,
    color: '#718096',
    marginLeft: 2,
  },

  /* Navigation bar */
  navBar: {
    position: 'sticky',
    bottom: 0,
    zIndex: 1000,
    backgroundColor: '#0d1117',
    borderTop: '1px solid #1a365d',
    padding: '10px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  backBtn: {
    padding: '8px 20px',
    borderRadius: 6,
    border: '1px solid #2d3748',
    backgroundColor: 'transparent',
    color: '#a0aec0',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
    flexShrink: 0,
  },
  nextBtn: {
    padding: '10px 28px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#4299e1',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background-color 0.15s',
    flexShrink: 0,
  },
  dots: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  finalBtns: {
    display: 'flex',
    alignItems: 'center',
  },
};
